/**
 * Practitioner service for managing practitioners
 */
import StorageService from './StorageService';
import authService from './AuthService';
import { USER_ROLES } from '../models/User';

class PractitionerService {
  /**
   * Constructor
   */
  constructor() {
    // We use the authService as practitioners are users with the PRACTITIONER role
    // Additional practitioner details are stored separately
    this.detailsStorageService = new StorageService('practitioner_details');
    this.userService = authService;
  }

  /**
   * Get all practitioners
   * @returns {Array} All practitioners
   */
  getAllPractitioners() {
    return this.userService.getAllUsers().filter(user =>
      user.role === USER_ROLES.PRACTITIONER
    );
  }

  /**
   * Get practitioner by ID
   * @param {string} id - Practitioner ID
   * @returns {Object|null} Practitioner or null if not found
   */
  getPractitionerById(id) {
    const practitioner = this.userService.getUserById(id);
    if (!practitioner || practitioner.role !== USER_ROLES.PRACTITIONER) {
      return null;
    }
    return practitioner;
  }

  /**
   * Create practitioner (creates a user with PRACTITIONER role)
   * @param {Object} practitionerData - Practitioner data
   * @returns {Object} Creation result
   */
  createPractitioner(practitionerData) {
    // Set the role to PRACTITIONER
    const userData = {
      ...practitionerData,
      role: USER_ROLES.PRACTITIONER
    };

    return this.userService.createUser(userData);
  }

  /**
   * Update practitioner
   * @param {string} id - Practitioner ID
   * @param {Object} practitionerData - Practitioner data to update
   * @returns {Object} Update result
   */
  updatePractitioner(id, practitionerData) {
    // First check if this is actually a practitioner
    const practitioner = this.getPractitionerById(id);
    if (!practitioner) {
      return { success: false, message: '未找到该医师' };
    }

    // Don't allow changing the role from this service
    const updateData = { ...practitionerData };
    delete updateData.role;

    return this.userService.updateUser(id, updateData);
  }

  /**
   * Delete practitioner
   * @param {string} id - Practitioner ID
   * @returns {Object} Deletion result
   */
  deletePractitioner(id) {
    // First check if this is actually a practitioner
    const practitioner = this.getPractitionerById(id);
    if (!practitioner) {
      return { success: false, message: '未找到该医师' };
    }

    return this.userService.deleteUser(id);
  }

  /**
   * Search practitioners by name or other fields
   * @param {string} query - Search query
   * @returns {Array} Matching practitioners
   */
  searchPractitioners(query) {
    if (!query) return this.getAllPractitioners();

    const lowercasedQuery = query.toLowerCase();

    return this.getAllPractitioners().filter(practitioner =>
      practitioner.name.toLowerCase().includes(lowercasedQuery) ||
      practitioner.username.toLowerCase().includes(lowercasedQuery) ||
      (practitioner.email && practitioner.email.toLowerCase().includes(lowercasedQuery)) ||
      (practitioner.phone && practitioner.phone.includes(lowercasedQuery))
    );
  }

  /**
   * Get practitioners who can perform a specific treatment
   * @param {string} treatmentId - Treatment ID
   * @returns {Array} Practitioners who can perform the treatment
   */
  getPractitionersForTreatment(treatmentId) {
    // This method requires integration with TreatmentService
    // Here we simply use the local storage directly for this MVP
    const treatments = new StorageService('treatments').getAll();
    const treatment = treatments[treatmentId];

    if (!treatment || !treatment.practitioners || !Array.isArray(treatment.practitioners)) {
      return [];
    }

    // Get practitioners by their IDs
    return treatment.practitioners
      .map(id => this.getPractitionerById(id))
      .filter(practitioner => practitioner !== null);
  }

  /**
   * Get practitioner's specific details (specialties, education, etc.)
   * @param {string} id - Practitioner ID
   * @returns {Object|null} Practitioner details or null if not found
   */
  getPractitionerDetails(id) {
    // First get the basic practitioner info
    const practitioner = this.getPractitionerById(id);
    if (!practitioner) return null;

    // Get additional details from practitioner details store
    const details = this.detailsStorageService.getById(id);

    return {
      ...practitioner,
      ...details
    };
  }

  /**
   * Set practitioner's details
   * @param {string} id - Practitioner ID
   * @param {Object} details - Practitioner details
   * @returns {boolean} Whether update was successful
   */
  setPractitionerDetails(id, details) {
    // First check if this is actually a practitioner
    const practitioner = this.getPractitionerById(id);
    if (!practitioner) return false;

    // Get current details or create new
    const currentDetails = this.detailsStorageService.getById(id) || {};

    // Merge with new details
    const updatedDetails = {
      ...currentDetails,
      ...details,
      id,
      updatedAt: new Date().toISOString()
    };

    // Save to storage
    return this.detailsStorageService.save(id, updatedDetails);
  }

  /**
   * Get practitioner's specialties
   * @param {string} id - Practitioner ID
   * @returns {Array} Practitioner specialties
   */
  getPractitionerSpecialties(id) {
    const details = this.getPractitionerDetails(id);
    return details?.specialties || [];
  }

  /**
   * Set practitioner's specialties
   * @param {string} id - Practitioner ID
   * @param {Array} specialties - Specialties array
   * @returns {boolean} Whether update was successful
   */
  setPractitionerSpecialties(id, specialties) {
    return this.setPractitionerDetails(id, { specialties });
  }

  /**
   * Get active practitioners count
   * @returns {number} Number of active practitioners
   */
  getPractitionerCount() {
    return this.getAllPractitioners().length;
  }

  /**
   * Get practitioners' schedule for a given date range
   * @param {string} practitionerId - Practitioner ID
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Array} Schedule entries for the practitioner
   */
  getPractitionerSchedule(practitionerId, startDate, endDate) {
    // This requires integration with AppointmentService
    // For MVP, we'll return an empty array
    return [];
  }

  /**
   * Check if a practitioner is available at a specific time
   * @param {string} practitionerId - Practitioner ID
   * @param {Date} startTime - Start time
   * @param {Date} endTime - End time
   * @returns {boolean} Whether practitioner is available
   */
  isPractitionerAvailable(practitionerId, startTime, endTime) {
    // This requires integration with AppointmentService
    // For MVP, we'll return true
    return true;
  }

  /**
   * Import practitioner details from JSON
   * @param {string} jsonData - JSON string of practitioner details
   * @param {boolean} [merge=false] - Whether to merge with existing data
   * @returns {boolean} Whether import was successful
   */
  importPractitionerDetails(jsonData, merge = false) {
    return this.detailsStorageService.importData(jsonData, merge);
  }

  /**
   * Export practitioner details as JSON
   * @returns {string} JSON string of practitioner details
   */
  exportPractitionerDetails() {
    return this.detailsStorageService.exportData();
  }
}

// Create singleton instance
const practitionerService = new PractitionerService();

export default practitionerService;